import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useAssetPermissionsCache } from '~/hooks/useAssetPermissionsCache';
import { useBoardPermissionsCache } from '~/hooks/useBoardPermissionsCache';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { selectedBoardIdsForShowcaseSelector, selectedClipsAssetIdsSelector } from '~/store/selectedItems/selectors';
import { canViewAsset } from '~/utils/permissions/assetPermissions';
import { canViewBoard } from '~/utils/permissions/boardPermissions';
import { canCreateCollection } from '~/utils/permissions/workspacePermissions';

export type UsePrivateCanShareCollectionResult = {
  /** A collection link can only cover items the recipient is allowed to be shown. */
  canShareCollection: boolean;
  /** How many items the link would carry. */
  itemCount: number;
};

export const usePrivateCanShareCollection = (): UsePrivateCanShareCollectionResult => {
  const assetIds = useSelector(selectedClipsAssetIdsSelector);
  const boardIds = useSelector(selectedBoardIdsForShowcaseSelector);

  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();
  const assetPermissionCache = useAssetPermissionsCache();
  const boardPermissionCache = useBoardPermissionsCache();

  const itemCount = assetIds.length + boardIds.length;

  const canShareCollection = useMemo(() => {
    if (itemCount === 0) {
      return false;
    }

    return (
      canCreateCollection(workspacePermissions) &&
      assetIds.every((assetId) => canViewAsset(assetPermissionCache.getAssetPermissions(assetId))) &&
      boardIds.every((boardId) => canViewBoard(boardPermissionCache.getBoardPermissions(boardId)))
    );
  }, [workspacePermissions, assetIds, boardIds, assetPermissionCache, boardPermissionCache, itemCount]);

  return { canShareCollection, itemCount };
};
